import React, { useEffect, useMemo, useState } from "react";
import SEO from "@/components/SEO";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, X, Trash2, Coins, AlertTriangle, Image as ImageIcon, ShieldCheck, Check, Lock, Sparkles } from "lucide-react";
import { customerApi, fmtErr } from "@/customer/api";
import { useCart } from "@/customer/CartContext";
import { useCustomerAuth } from "@/customer/AuthContext";

const fmtN = (n) => (n ?? 0).toLocaleString();

const ConfirmModal = ({ cart, onClose, onConfirm, submitting }) => {
  return (
    <>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50" onClick={onClose} data-testid="cart-confirm-overlay" />
      <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-3 sm:p-6 overflow-y-auto pointer-events-none">
        <div data-testid="cart-confirm-modal" className="glass-strong chrome-border rounded-lg w-full max-w-xl pointer-events-auto my-auto" onClick={(e) => e.stopPropagation()}>
          <div className="px-5 sm:px-6 py-4 border-b border-white/5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full chrome-border bg-black/40 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-[#ff2e93]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] tracking-luxe uppercase text-white/45">Confirm purchase</p>
              <p className="font-display text-lg text-chrome">Use {cart.total_credits} credits</p>
            </div>
            <button onClick={onClose} data-testid="cart-confirm-close" className="w-8 h-8 rounded-full border border-white/10 text-white/70 hover:text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          </div>
          <div className="p-5 sm:p-6 space-y-4">
            <p className="text-sm text-white/80">
              Confirm purchase using <strong className="text-[#ff2e93]">{cart.total_credits} credits</strong>?
              Your credits will be deducted and these photos will be added to your purchased photos.
              Full-quality downloads will be enabled once the secure download system is activated.
            </p>
            <div className="glass chrome-border rounded-md divide-y divide-white/5">
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-white/55">Photos</span><span className="text-white">{cart.selected_count}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-white/55">Total credits</span><span className="text-[#ff2e93] font-display">{cart.total_credits}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-white/55">Available now</span><span className="text-white">{cart.available_credits}</span>
              </div>
              <div className="px-4 py-3 flex justify-between text-sm">
                <span className="text-white/55">Remaining after purchase</span><span className="text-[#7fe6ff] font-display">{cart.remaining_after_purchase}</span>
              </div>
            </div>
            <div className="flex flex-col-reverse sm:flex-row gap-3 pt-2">
              <button onClick={onClose} data-testid="cart-confirm-cancel" className="btn-ghost !py-2.5 !px-5 !text-[11px] flex-1">Cancel</button>
              <button onClick={onConfirm} disabled={submitting} data-testid="cart-confirm-submit" className="btn-primary !py-2.5 !px-5 !text-[11px] flex-1 disabled:opacity-60">
                <Check className="w-3.5 h-3.5" /> {submitting ? "Processing…" : `Confirm · ${cart.total_credits} cr`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const SuccessModal = ({ data, onClose }) => {
  return (
    <>
      <div className="fixed inset-0 bg-black/85 backdrop-blur-sm z-50" data-testid="cart-success-overlay" />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
        <div data-testid="cart-success-modal" className="glass-strong chrome-border rounded-lg w-full max-w-lg my-auto text-center">
          <div className="p-7 sm:p-9">
            <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#ff2e93] to-[#18d3ff] mx-auto flex items-center justify-center mb-5">
              <Check className="w-6 h-6 text-white" />
            </div>
            <p className="text-[10px] tracking-luxe uppercase text-white/45">— Purchase Complete</p>
            <h2 className="font-display text-3xl sm:text-4xl text-chrome mt-2">Your moments are yours.</h2>
            <p className="text-sm text-white/70 mt-3">
              {data.photo_count} photo{data.photo_count === 1 ? "" : "s"} added to your purchased photos.
              Full-quality downloads will be available once the secure download system is activated.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3 text-left">
              <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Credits used</p><p className="font-display text-2xl text-[#ff7fc1]">{data.total_credits_used}</p></div>
              <div className="glass chrome-border rounded-md p-3"><p className="text-[10px] tracking-luxe uppercase text-white/45">Remaining balance</p><p className="font-display text-2xl text-[#7fe6ff]">{data.balance_after}</p></div>
            </div>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Link to="/customer/purchased" onClick={onClose} data-testid="cart-success-view-purchased" className="btn-primary !py-2.5 !px-5 !text-[11px] flex-1">
                <ShoppingBag className="w-3.5 h-3.5" /> View Purchased
              </Link>
              <Link to="/events" onClick={onClose} data-testid="cart-success-browse" className="btn-ghost !py-2.5 !px-5 !text-[11px] flex-1">
                Browse More Events
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default function CustomerCart() {
  const navigate = useNavigate();
  const { refresh: refreshAuth } = useCustomerAuth();
  const { cart, refresh, removeItem, clear, loading } = useCart();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  useEffect(() => { refresh(); }, [refresh]);

  const hasIssues = useMemo(() => {
    if (!cart) return false;
    return (cart.has_blocking_pricing_issue) ||
      (cart.skipped_purchased?.length) || (cart.skipped_duplicates?.length);
  }, [cart]);

  const handleConfirm = async () => {
    if (!cart) return;
    setSubmitting(true);
    try {
      const r = await customerApi.post("/customer/checkout", {
        expected_total_credits: cart.total_credits,
      });
      setSuccess(r.data);
      setConfirmOpen(false);
      await Promise.all([refresh(), refreshAuth?.()]);
      toast.success("Purchase complete.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) {
        toast.error("Pricing changed — refreshing your cart…");
        await refresh();
      } else {
        toast.error(fmtErr(e));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (photoId) => {
    try {
      await removeItem(photoId);
      toast.success("Removed from cart.");
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const handleClear = async () => {
    if (!window.confirm("Clear all items from your cart?")) return;
    try { await clear(); toast.success("Cart cleared."); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div data-testid="page-customer-cart" className="max-w-[1280px] mx-auto px-5 sm:px-8 pt-32 pb-24 space-y-8">
      <SEO title="CustomerCart · WelkomBiz Photography" noindex />
      <header className="space-y-3">
        <Link to="/events" data-testid="cart-back" className="inline-flex items-center gap-2 text-[10px] tracking-luxe uppercase text-white/55 hover:text-white">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Events
        </Link>
        <p className="text-[11px] tracking-luxe uppercase text-white/45">— Your Cart</p>
        <h1 className="font-display text-4xl sm:text-5xl leading-[1.05]">
          <span className="text-chrome">Selected </span>
          <span className="italic text-magcyan">photos</span>
          <span className="text-chrome">.</span>
        </h1>
      </header>

      {loading && !cart ? (
        <p className="text-sm text-white/40">Loading your cart…</p>
      ) : !cart || cart.selected_count === 0 ? (
        <div data-testid="cart-empty" className="glass chrome-border rounded-md p-12 text-center">
          <ShoppingBag className="w-7 h-7 text-[#ff2e93] mx-auto mb-4" />
          <h2 className="font-display text-2xl text-chrome">Your cart is empty</h2>
          <p className="text-sm text-white/55 mt-2">Browse event galleries and select your favourite moments.</p>
          <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/events" data-testid="cart-empty-browse" className="btn-primary !py-2.5 !px-5 !text-[11px]"><ShoppingBag className="w-3.5 h-3.5" /> Browse Events</Link>
            <Link to="/customer" data-testid="cart-empty-dashboard" className="btn-ghost !py-2.5 !px-5 !text-[11px]">Go to Dashboard</Link>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] gap-6">
          {/* Items grouped by event */}
          <div className="space-y-6">
            {cart.groups.map((g) => (
              <section key={g.event.id} data-testid={`cart-group-${g.event.id}`} className="glass chrome-border rounded-md">
                <header className="px-5 py-4 border-b border-white/5 flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] tracking-luxe uppercase text-white/45">— Event</p>
                    <h2 className="font-display text-xl sm:text-2xl text-chrome">{g.event.title}</h2>
                    <p className="text-[10px] tracking-luxe uppercase text-white/45 mt-1">{g.event.date} · {g.selected_count} photo{g.selected_count === 1 ? "" : "s"}</p>
                  </div>
                  {!g.pricing_available && (
                    <span data-testid={`cart-group-no-pricing-${g.event.id}`} className="inline-flex items-center gap-1 text-[10px] tracking-luxe uppercase px-2 py-1 rounded-full text-[#ffd479] border border-[#ffd479]/30">
                      <AlertTriangle className="w-3 h-3" /> Pricing not active
                    </span>
                  )}
                </header>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
                  {g.items.map((it) => (
                    <div key={it.cart_item_id} data-testid={`cart-item-${it.photo_id}`} className="relative photo-frame chrome-border aspect-square overflow-hidden">
                      {it.thumbnail_url
                        ? <img src={it.thumbnail_url} alt={it.display_name} loading="lazy" className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-black/60 flex items-center justify-center text-white/30"><ImageIcon className="w-5 h-5" /></div>}
                      <button
                        data-testid={`cart-item-remove-${it.photo_id}`}
                        onClick={() => handleRemove(it.photo_id)}
                        className="absolute top-2 right-2 w-7 h-7 rounded-full glass-strong border border-white/20 text-white/85 hover:text-[#ff7fc1] flex items-center justify-center"
                        aria-label="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute inset-x-0 bottom-0 p-2" style={{background:"linear-gradient(180deg,transparent,rgba(0,0,0,0.85))"}}>
                        <p className="text-[10px] tracking-luxe uppercase text-white truncate">{it.display_name}</p>
                        {it.subfolder_name && <p className="text-[9px] tracking-luxe uppercase text-white/55 truncate">{it.subfolder_name}</p>}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="px-5 py-4 border-t border-white/5 bg-white/3">
                  <p className="text-[10px] tracking-luxe uppercase text-white/45 mb-2">Pricing Breakdown</p>
                  {g.pricing_available && g.breakdown && g.breakdown.length > 0 ? (
                    <ul data-testid={`cart-breakdown-${g.event.id}`} className="text-xs text-white/80 space-y-1">
                      {g.breakdown.map((b, idx) => (
                        <li key={idx} className="flex justify-between gap-3">
                          <span>{b.qty} × {b.label} <span className="text-white/40">@ {b.unit_credits} cr</span></span>
                          <span className="text-white">{b.subtotal} cr</span>
                        </li>
                      ))}
                      <li className="flex justify-between gap-3 pt-2 border-t border-white/5 mt-2">
                        <span className="text-[10px] tracking-luxe uppercase text-white/55">Event total</span>
                        <span className="font-display text-lg text-[#ff7fc1]">{g.total_credits} cr</span>
                      </li>
                      {g.savings > 0 && (
                        <li data-testid={`cart-savings-${g.event.id}`} className="text-[10px] tracking-luxe uppercase text-[#7fe6ff]">
                          You saved {g.savings} credits with package pricing.
                        </li>
                      )}
                    </ul>
                  ) : (
                    <p className="text-xs text-[#ffd479]">
                      Pricing for this event is not available yet. Please contact WelkomBiz Photography.
                    </p>
                  )}
                </div>
              </section>
            ))}

            {hasIssues && (
              <div data-testid="cart-warnings" className="glass chrome-border rounded-md p-4 text-xs text-[#ffd479]">
                {cart.skipped_purchased?.length > 0 && (
                  <p>{cart.skipped_purchased.length} photo(s) you already purchased were excluded — you will not be charged again.</p>
                )}
                {cart.skipped_duplicates?.length > 0 && (
                  <p>Duplicate selections were collapsed.</p>
                )}
                {cart.warnings?.map((w, i) => <p key={i}>{w}</p>)}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <button data-testid="cart-clear" onClick={handleClear} className="btn-ghost !py-2.5 !px-5 !text-[11px]"><Trash2 className="w-3.5 h-3.5" /> Clear Cart</button>
              <Link to="/events" data-testid="cart-continue" className="btn-ghost !py-2.5 !px-5 !text-[11px]"><ArrowLeft className="w-3.5 h-3.5" /> Continue Browsing</Link>
            </div>
          </div>

          {/* Summary */}
          <aside className="space-y-4 lg:sticky lg:top-28 lg:self-start">
            <div data-testid="cart-summary" className="glass-strong chrome-border rounded-md p-5 space-y-4">
              <div>
                <p className="text-[10px] tracking-luxe uppercase text-white/45">Available Credits</p>
                <p className="font-display text-4xl text-magcyan">{fmtN(cart.available_credits)}</p>
              </div>
              <div className="border-t border-white/5 pt-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-white/55">Photos</span><span className="text-white">{cart.selected_count}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/55">Total credits</span><span className="text-[#ff7fc1] font-display text-lg" data-testid="cart-summary-total">{cart.total_credits} cr</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-white/55">After purchase</span>
                  <span className={`${cart.remaining_after_purchase < 0 ? "text-[#ff7fc1]" : "text-[#7fe6ff]"} font-display text-lg`} data-testid="cart-summary-remaining">{cart.remaining_after_purchase} cr</span>
                </div>
              </div>

              {!cart.sufficient_credits && (
                <div data-testid="cart-insufficient" className="glass chrome-border rounded-md p-3 text-xs text-[#ff9fcf]">
                  <AlertTriangle className="w-3.5 h-3.5 inline mr-1.5" />
                  You need {Math.max(0, cart.total_credits - cart.available_credits)} more credit(s). Please contact WelkomBiz Photography to add credits to your account.
                </div>
              )}
              {cart.has_blocking_pricing_issue && (
                <div data-testid="cart-pricing-blocked" className="glass chrome-border rounded-md p-3 text-xs text-[#ffd479]">
                  <Lock className="w-3.5 h-3.5 inline mr-1.5" />
                  One or more events do not yet have active pricing. Checkout is disabled.
                </div>
              )}

              <button
                onClick={() => setConfirmOpen(true)}
                disabled={!cart.sufficient_credits || cart.has_blocking_pricing_issue || cart.selected_count === 0}
                data-testid="cart-checkout"
                className="btn-primary w-full !py-3 !text-[11px] disabled:opacity-50"
              >
                <ShoppingBag className="w-3.5 h-3.5" /> Confirm Purchase
              </button>
              <p className="text-[10px] tracking-luxe uppercase text-white/40 text-center">
                Credits only — no online payment in this phase.
              </p>
            </div>

            <div className="glass chrome-border rounded-md p-4 flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-[#ff2e93] mt-0.5 shrink-0" />
              <p className="text-[11px] text-white/55">
                Credit deductions happen securely on the server. Originals are never exposed before secure download is activated.
              </p>
            </div>
          </aside>
        </div>
      )}

      {confirmOpen && cart && <ConfirmModal cart={cart} onClose={() => setConfirmOpen(false)} onConfirm={handleConfirm} submitting={submitting} />}
      {success && <SuccessModal data={success} onClose={() => { setSuccess(null); navigate("/customer/purchased"); }} />}
    </div>
  );
}
